import React from 'react';

const Section8CostPerformance = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-yellow-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">💰</span>
      <span className="bg-gradient-to-r from-yellow-600 to-lime-600 bg-clip-text text-transparent">
        Cost & Performance Optimization
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">Production thinking: real-world constraints matter</p>

    {/* Per-model comparison table */}
    <div className="bg-gradient-to-br from-yellow-50/50 to-lime-50/50 rounded-2xl p-6 border border-yellow-200/30 mb-6">
      <h3 className="text-base font-bold text-yellow-600 uppercase tracking-wider mb-5 text-center">Per-Model Cost & Speed Comparison</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-yellow-50 to-lime-50">
              <th className="px-4 py-3 text-left text-sm font-bold text-yellow-700 uppercase tracking-wider">Metric</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-blue-600 uppercase tracking-wider">⚡ Gemini</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-orange-600 uppercase tracking-wider">🎨 Claude</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-green-600 uppercase tracking-wider">🤖 OpenAI</th>
            </tr>
          </thead>
          <tbody>
            {[
              { metric: 'Avg Response Time', gemini: '21.8s', claude: '15.8s', openai: '6.5s' },
              { metric: 'Best Response Time', gemini: '12.2s', claude: '1.3s', openai: '793ms' },
              { metric: 'Avg Cost / Request', gemini: '$0.0067', claude: '$0.0145', openai: '$0.0019' },
              { metric: 'Avg Quality Score', gemini: '90.4', claude: '88.5', openai: '87.0' },
              { metric: 'Best For', gemini: 'Highest quality', claude: 'Most creative', openai: 'Speed + cost + agents' },
            ].map((row, i) => (
              <tr key={i} className="border-b border-yellow-100/50 hover:bg-yellow-50/30 transition-colors duration-200">
                <td className="px-4 py-3 text-sm font-bold text-gray-700">{row.metric}</td>
                <td className="px-4 py-3 text-sm text-gray-600 text-center">{row.gemini}</td>
                <td className="px-4 py-3 text-sm text-gray-600 text-center">{row.claude}</td>
                <td className="px-4 py-3 text-sm text-gray-600 text-center">{row.openai}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* What we track */}
    <div className="bg-gradient-to-br from-yellow-50/50 to-lime-50/50 rounded-2xl p-6 border border-yellow-200/30 mb-6">
      <h3 className="text-base font-bold text-yellow-600 uppercase tracking-wider mb-4 text-center">What We Track Per Generation</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: '📥', label: 'Input Tokens', desc: 'Prompt size sent to model' },
          { icon: '📤', label: 'Output Tokens', desc: 'Response size from model' },
          { icon: '💵', label: 'Cost (USD)', desc: 'Calculated from model pricing' },
          { icon: '⏱️', label: 'Response Time', desc: 'End-to-end latency in ms' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-yellow-100/50 text-center hover:scale-105 transition-transform duration-300">
            <span className="text-2xl block mb-1">{item.icon}</span>
            <p className="text-sm font-bold text-gray-800">{item.label}</p>
            <p className="text-xs text-gray-400 mt-1">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Optimization strategies */}
    <div className="bg-gradient-to-r from-yellow-50/80 to-lime-50/80 rounded-2xl p-5 border border-yellow-200/30">
      <h3 className="text-base font-bold text-yellow-600 uppercase tracking-wider mb-3">🎯 Optimization Strategies</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Pre-computed vendor profiles', a: 'AI profiling runs once during vendor onboarding. Runtime matching is pure math (cosine similarity). Zero AI API calls, 50ms response.' },
          { q: 'Non-blocking embeddings', a: 'Theme embedding happens after save completes. If embedding fails, the save still succeeds. Users never wait for vector generation.' },
          { q: 'Graceful error handling', a: 'Single Model mode notifies users to try another model on failure. No silent fallbacks. Compare Mode uses Promise.allSettled(). Partial results returned if one model fails.' },
          { q: 'Agent tool caching', a: 'Model performance data cached in-memory with 5-min TTL. Repeated "which model is better?" queries hit cache, not database.' },
          { q: 'Rate limiting', a: 'Embedding scripts use 100ms delay between API calls. Auth endpoints have stricter rate limits. Prevents cost spikes from abuse.' },
          { q: 'Smart token management', a: 'Each prompt strategy is designed for token efficiency. Few-Shot uses fewer tokens than Hybrid Reasoning. A/B testing measures if the extra cost is worth the quality gain.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-yellow-100/50">
            <p className="text-sm font-bold text-yellow-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>

);

export default Section8CostPerformance;
